import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../../../../../App/redux/store';
import { adMarkerBuilder } from '../../../../adMarkerBuilder';
import { adActions, getAdState } from '../../../../adSlice';
import { getSelectedAdDetailsFormTabState } from '../../../../../../store/appManager/reducer';
import AdMarker from './AdMarker';
import { GeoJSON } from 'react-leaflet';

function AdMarkers() {
  const { similarAdPlots, similarAdDpes } = useAppSelector(getAdState, shallowEqual);
  const selectedTab = useAppSelector(getSelectedAdDetailsFormTabState);
  const dispatch = useAppDispatch();

  // Combine similar plots and DPE data to create unified markers and geometry
  const { combinedMarkers, geometryData } = useMemo(() => {
    const plots = similarAdPlots.result || [];
    const dpes = similarAdDpes.result || [];

    // Create address to plot mapping for quick lookup
    const plotsByAddress = new Map();
    plots.forEach((plot) => {
      const key = plot.address.toLowerCase().trim();
      plotsByAddress.set(key, plot);
    });

    // Remove duplicate DPE entries
    const uniqueDpes = dpes.reduce((acc: AdDpe[], current: AdDpe) => {
      const existingIndex = acc.findIndex(
        (item) => item.uniqueDpeId === current.uniqueDpeId,
      );
      if (existingIndex === -1) {
        acc.push(current);
      }
      return acc;
    }, []);

    const markers: any[] = [];
    const geometry: any[] = [];
    const processedPlots = new Set();

    // Process DPEs first to identify combinations
    uniqueDpes.forEach((dpe) => {
      const dpeAddress = dpe.isGeoAddress ? dpe.address : null;
      const matchingPlot = dpeAddress
        ? plotsByAddress.get(dpeAddress.toLowerCase().trim())
        : null;

      if (matchingPlot) {
        // Create combined marker
        const combinedId = `combined_${matchingPlot.fullPlotId}_${dpe.uniqueDpeId}`;
        markers.push(
          adMarkerBuilder({
            id: combinedId,
            coordinates: matchingPlot.coordinates,
            types: ['dpe-area-combined'],
            onClick: () => {
              // Smart scrolling based on active tab
              if (selectedTab === 0) {
                // Parcelle tab - scroll to plot
                dispatch(adActions.highlightedPlotIdSet(matchingPlot.fullPlotId));
                const element = document.getElementById(matchingPlot.fullPlotId);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              } else {
                // DPE tab - scroll to DPE
                dispatch(adActions.highlightedDpeIdSet(dpe.uniqueDpeId));
                dispatch(adActions.highlightedDpeIdIsScrollingSet(true));
                const element = document.getElementById(dpe.uniqueDpeId);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }
            },
          }),
        );

        // Add red geometry for combined marker
        geometry.push({
          id: combinedId,
          geometry: matchingPlot.geometry,
          isCombined: true,
        });

        processedPlots.add(matchingPlot.fullPlotId);
      } else {
        // Create standalone DPE marker
        markers.push(
          adMarkerBuilder({
            id: dpe.uniqueDpeId,
            coordinates: dpe.coordinates,
            types: dpe.types,
            onClick: () => {
              dispatch(adActions.highlightedDpeIdSet(dpe.uniqueDpeId));
              dispatch(adActions.highlightedDpeIdIsScrollingSet(true));
              const element = document.getElementById(dpe.uniqueDpeId);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            },
          }),
        );
      }
    });

    // Add standalone plot markers for plots without matching DPEs
    plots.forEach((plot) => {
      if (!processedPlots.has(plot.fullPlotId)) {
        markers.push(
          adMarkerBuilder({
            id: plot.fullPlotId,
            coordinates: plot.coordinates,
            types: plot.types,
            onClick: () => {
              dispatch(adActions.highlightedPlotIdSet(plot.fullPlotId));
              const element = document.getElementById(plot.fullPlotId);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            },
          }),
        );

        // Add yellow geometry for standalone plot
        geometry.push({
          id: plot.fullPlotId,
          geometry: plot.geometry,
          isCombined: false,
        });
      }
    });

    return { combinedMarkers: markers, geometryData: geometry };
  }, [similarAdPlots.result, similarAdDpes.result, selectedTab, dispatch]);

  return (
    <>
      {/* Combined and standalone markers */}
      {combinedMarkers.map((m) => (
        <AdMarker key={m.id} datas={{ ...m }} />
      ))}

      {/* Geometry with color coding */}
      {geometryData.map((g) => (
        <GeoJSON
          data={
            {
              type: 'Feature',
              geometry: g.geometry,
              properties: {},
            } as GeoJSON.Feature
          }
          key={g.id}
          style={{
            fillColor: g.isCombined ? 'red' : 'yellow',
            weight: 3,
            color: g.isCombined ? 'red' : 'yellow',
            fillOpacity: 0.4,
          }}
        />
      ))}
    </>
  );
}

export default AdMarkers;
